import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from "@angular/core";
import { StateService, UIRouterGlobals } from "@uirouter/core";
import { debounce } from "lodash";
import { forkJoin } from "rxjs";
import { finalize, tap } from "rxjs/operators";
import { environment as appEnv } from "../../../../environments/environment";
import { NotifierPropertiesService } from "../../../models/notifier-properties.service";
import { QueriesService } from "../../../models/queries.service";
import { OutdatedNotifierService } from "../../../outdated-notifier/outdated-notifier.service";
import { RememberService } from "../../../remember/remember.service";
import { RoundTimeService } from "../../../round-time/round-time.service";

@Component({
  selector: "ab-query-table",
  templateUrl: "./query-table.component.html",
})
export class QueryTableComponent implements OnChanges, OnInit {
  @Input() public route: any;
  @Input() public timeDurAgo: string;
  @Input() public dur: string;
  @Input() public project: any;

  public loading: boolean;
  public queries: any;
  public currentPage: number;
  public totalPages: number;
  public totalQueries: number;
  public sortColumn: string;
  public sortDesc: boolean;
  public search: string;
  public appEnv = appEnv;
  public debounceSearchQuery = debounce(() => {
    this.searchQuery();
  }, 1500);
  public needsNotifierUpgrade: boolean;
  public notifierName: string;

  private DEFAULT_SORT_COLUMN = "stat.p50";
  private REMEMBER_SORT_COLUMN = "projectPerformance.route.queries.sortColumn";
  private REMEMBER_SORT_DESC = "projectPerformance.route.queries.sortDesc";
  private PAGE_SIZE = 20;

  constructor(
    private queriesService: QueriesService,
    private roundTimeService: RoundTimeService,
    private $uiRouterGlobals: UIRouterGlobals,
    private $state: StateService,
    private rememberService: RememberService,
    private notifierPropertiesService: NotifierPropertiesService,
    private outdatedNotifierService: OutdatedNotifierService,
  ) {}

  public ngOnInit() {
    this.currentPage = Number(this.$uiRouterGlobals.params.page || 1);
    this.sortColumn =
      this.$uiRouterGlobals.params.sortColumn ||
      this.rememberService.get(
        this.REMEMBER_SORT_COLUMN,
        this.DEFAULT_SORT_COLUMN,
      );
    this.sortDesc = this.sortDescending();
    this.search = this.$uiRouterGlobals.params.search;

    const queriesObservable = this.setQueries();

    this.needsNotifierUpgrade = false;
    const params = { projectId: this.project.id };
    const notifierPropsObservable = this.notifierPropertiesService
      .get(params)
      .pipe(
        tap((data: any) => {
          const name = data.notifier_name;
          const version = data.notifier_version;

          this.notifierName = data.notifier_name;
          if (this.outdatedNotifierService.isOutdatedForAPM(name, version)) {
            this.needsNotifierUpgrade = true;
          }
        }),
      );

    forkJoin([queriesObservable, notifierPropsObservable]).subscribe();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.timeDurAgo.isFirstChange()) {
      return;
    }

    this.setQueries().subscribe();
  }

  public trackBy(_i: number, query: any) {
    return query.queryId;
  }

  public loadPage(page: number, params = {} as any) {
    this.currentPage = page;
    this.$state.go(this.$uiRouterGlobals.current.name, { ...params, page });

    this.setQueries().subscribe();
  }

  public sortBy(column: string) {
    this.sortDesc =
      column === this.sortColumn
        ? !this.sortDesc
        : this.defaultSortDesc(column);
    this.rememberService.set(this.REMEMBER_SORT_DESC, this.sortDesc);

    this.sortColumn = column;
    this.rememberService.set(this.REMEMBER_SORT_COLUMN, this.sortColumn);

    const params = { sortColumn: this.sortColumn, sortDesc: this.sortDesc };
    this.loadPage(1, params);
  }

  public shouldShowCTA(): boolean {
    if (
      this.needsNotifierUpgrade &&
      this.queries &&
      this.queries.length === 0
    ) {
      return true;
    } else {
      return false;
    }
  }

  private defaultSortDesc(column: string) {
    // Default sort for query which sorts by (file, line, query) is ascending
    // and for others descending.
    return column !== "query";
  }

  private sortDescending() {
    const sortDescParam = this.$uiRouterGlobals.params.sortDesc;
    if (sortDescParam) {
      return sortDescParam === "true";
    }

    return this.rememberService.get(
      this.REMEMBER_SORT_DESC,
      this.defaultSortDesc(this.sortColumn),
    ) as boolean;
  }

  private setQueries() {
    this.loading = true;

    const params = {
      projectId: this.route.projectId,
      route_id: this.route.routeId,
      time__gte: this.roundTimeService.timestampFor(this.timeDurAgo, this.dur),
      page: this.currentPage,
      limit: this.PAGE_SIZE,
      order: this.sortColumn,
    } as any;

    if (this.sortDesc) {
      params.order += " desc";
    }
    if (this.search) {
      params.query__ieq = this.search;
    }

    return this.queriesService.query(params).pipe(
      tap((data: any) => {
        this.queries = data.queries;
        this.totalQueries = data.count;
        this.totalPages = Math.ceil(this.totalQueries / this.PAGE_SIZE);
      }),
      finalize(() => {
        this.loading = false;
      }),
    );
  }

  private searchQuery() {
    this.currentPage = 1;
    this.$state.go(this.$uiRouterGlobals.current.name, {
      search: this.search,
      page: 1,
    });
    this.setQueries().subscribe();
  }
}
